// webcomponents/site-footer.js
import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

// Define the icons as constants
const telegramIcon = unsafeHTML(`<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.693.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
</svg>`);

const redditIcon = unsafeHTML(`<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
  <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.072 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.33.33 0 0 0-.231-.095z"/>
</svg>`);

const tailwindStyles = css`${unsafeCSS(COMPONENT_STYLES)}`;

class SiteFooter extends LitElement {
  static properties = {
    sitePath: { type: String },
    currentYear: { type: String },
  };

  static styles = tailwindStyles;

  constructor() {
    super();
    this.sitePath = '/';
    this.currentYear = new Date().getFullYear().toString();
    this.telegram = 'https://t.me/plusefin';
    this.reddit = 'https://www.reddit.com/r/plusefin/';
    this.isDashboardDomain = window.location.hostname === 'console.plusefin.com';
  }

  render() {
    return html`
      <footer class="border-t border-[#e2d4c4] py-12 md:px-8 ${this.isDashboardDomain ? 'bg-white' : 'bg-[#fdfaf6]'}">
        <div class="container mx-auto px-4">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div class="col-span-1 md:col-span-2">
              <a class="mb-4 flex items-center" href="${this.sitePath || '/'}">
                <img class="h-9 w-9" src="${this.sitePath}/favicon.svg" alt="PlusE logo" />
                <span class="ml-2 text-xl font-bold text-[#241f1b]">PlusE</span>
              </a>
              <p class="mb-4 max-w-md text-[#675c51]">
                The financial data layer for AI agents: searchable, structured, point-in-time market context for reasoning workflows.
              </p>
              <div class="flex space-x-4">
                <a href="${this.telegram}" target="_blank" rel="noreferrer" class="text-[#675c51] transition-colors hover:text-[#241f1b]">
                  ${telegramIcon}
                  <span class="sr-only">Telegram</span>
                </a>
                <a href="${this.reddit}" target="_blank" rel="noreferrer" class="text-[#675c51] transition-colors hover:text-[#241f1b]">
                  ${redditIcon}
                  <span class="sr-only">Reddit</span>
                </a>
              </div>
            </div>

            <div>
              <h4 class="mb-4 text-sm font-semibold text-[#241f1b]">Product</h4>
              <ul class="space-y-2">
                <li><a href="${this.sitePath}/#data-layer" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Data Layer</a></li>
                <li><a href="${this.sitePath}/#capabilities" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Capabilities</a></li>
                <li><a href="${this.sitePath}/docs" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Documentation</a></li>
                <li><a href="${this.sitePath}/#pricing" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h4 class="mb-4 text-sm font-semibold text-[#241f1b]">Support</h4>
              <ul class="space-y-2">
                <li><a href="${this.telegram}" target="_blank" rel="noreferrer" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Telegram Community</a></li>
                <li><a href="${this.reddit}" target="_blank" rel="noreferrer" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Reddit Community</a></li>
                <li><a href="${this.sitePath}/docs/mcp-client-setup" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">MCP Setup</a></li>
                <li><a href="mailto:support@plusefin.com" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Contact Us</a></li>
              </ul>
            </div>
          </div>

          <div class="flex flex-col items-center justify-between border-t border-[#e2d4c4] pt-8 md:flex-row">
            <p class="mb-4 text-sm text-[#675c51] md:mb-0">
              © ${this.currentYear} PlusE Finance. All rights reserved.
            </p>
            <div class="flex space-x-6">
              <a href="${this.sitePath}/privacy" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Privacy Policy</a>
              <a href="${this.sitePath}/terms" class="text-sm text-[#675c51] transition-colors hover:text-[#241f1b]">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    `;
  }
}

customElements.define('site-footer', SiteFooter);
